import { memo, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LoadingComponent from "../../components/LoadingComponent";
import DeliveryItem from "../../components/DeliveryItem";
import { useAuth } from "../../hooks/useAuth";
import { useDriverDeliveries } from "../../hooks/useDriverDeliveries";
import { isToday, formatDate } from "../../utils/functions";

const MyRoutes = () => {
  const { user, logout } = useAuth();
  const {
    deliveries,
    loading,
    error,
    auxSearcher,
    setAuxSearcher,
    startPendingRoutes,
    selectedDate,
    goToPreviousDay,
    goToNextDay,
    goToToday,
  } = useDriverDeliveries();
  const stats = useMemo(() => {
    if (!deliveries || deliveries.length === 0) {
      return { total: 0, delivered: 0, failed: 0 };
    }

    return {
      total: deliveries.length,
      delivered: deliveries.filter(
        (delivery) => delivery.status === "delivered",
      ).length,
      failed: deliveries.filter((delivery) => delivery.status === "failed")
        .length,
    };
  }, [deliveries]);

  const hasPendingDeliveries = useMemo(
    () =>
      deliveries.some(
        (delivery) =>
          delivery.status === "assigned" || delivery.status === "pending"
        || delivery.status === "withdrawn" || delivery.status === "in_cd" || delivery.status === "created_on_courier" 
        || delivery.status === "waiting_for_assignment"
        || delivery.status === "fulfilled" || delivery.status === "ready_for_pickup"
        || delivery.status === "dropped_at_hub",
      ),
    [deliveries],
  );

  const isSelectedDateToday = isToday(selectedDate);
  const previousDate = new Date(selectedDate);
  previousDate.setDate(selectedDate.getDate() - 1);
  const nextDate = new Date(selectedDate);
  nextDate.setDate(selectedDate.getDate() + 1);

  return (
    <Box sx={{ p: 0.5, backgroundColor: "#fff", minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6">Hola, {user.full_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {user.role}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="error"
          size="small"
          sx={{
            backgroundColor: "#ff6666 !important",
            borderRadius: "0px !important",
            border: "0px !important",
          }}
          onClick={logout}
        >
          Salir
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <Button
          onClick={goToPreviousDay}
          sx={{ padding: "5px !important", fontSize: "1rem !important" }}
        >
          {formatDate(previousDate)}
        </Button>
        <Button
          onClick={goToToday}
          sx={{
            padding: "5px !important",
            fontSize: "1rem !important",
            backgroundColor: "#ccc",
            color: "#363434ff !important",
          }}
        >
          {formatDate(selectedDate)}
        </Button>
        <Button
          onClick={goToNextDay}
          disabled={isSelectedDateToday}
          sx={{ padding: "5px !important", fontSize: "1rem !important" }}
        >
          {isSelectedDateToday ? null : formatDate(nextDate)}
        </Button>
      </Box>

      <Box sx={{ mb: 2, mt: 1 }}>
        <TextField
          fullWidth
          placeholder="Buscar por cliente, orden, dirección..."
          value={auxSearcher}
          onChange={(e) => setAuxSearcher(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            style: { borderRadius: "0px" },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "0px !important",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#ccc",
            },
            backgroundColor: "#f9f9f9",
            marginTop: "5px !important"
          }}
        />
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          mb: 2,
          borderTop: "0px solid #e0e0e0",
          borderBottom: "0px solid #e0e0e0",
          py: 1,
        }}
      >
        <Typography variant="body2">
          Mis entregas de hoy ({stats.total})
        </Typography>
        <Typography variant="body2">Entregadas ({stats.delivered})</Typography>
        <Typography variant="body2">Fallidas({stats.failed})</Typography>
      </Stack>

      <Button
        variant="contained"
        fullWidth
        sx={{
          py: 1,
          mb: 2,
          minWidth: "100% !important",
          borderRadius: "0px !important",
          backgroundColor: "#F5B01C !important",
          color: "white",
          fontWeight: "bolder !important",
          fontSize: "1rem",
          "&:hover": {
            backgroundColor: "#E0A010 !important",
          },
        }}
        disabled={!hasPendingDeliveries}
        onClick={() => startPendingRoutes()}
      >
        INICIAR RUTA
      </Button>

      <Stack spacing={1.5}>
        {loading ? (
          <LoadingComponent />
        ) : deliveries.length > 0 ? (
          <>
            {error ? (
              <Alert sx={{ borderRadius: "0px !important" }} severity="error">
                {error}
              </Alert>
            ) : null}
            {deliveries.map((delivery) => (
              <DeliveryItem key={delivery.id} delivery={delivery} />
            ))}
          </>
        ) : (
          <Typography
            sx={{ textAlign: "center", p: 4, color: "text.secondary" }}
          >
            No hay entregas para la fecha seleccionada.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default memo(MyRoutes);
